import {useMemo} from 'react';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import {
  Menu,
  MenuItem,
  MenuItemIcon,
  menuItemIconStyle,
  MenuItemSpacer,
  MenuItemTrailingIcon,
  MenuItemTypography
} from 'patient-ping-remedy/packages/menu';
import ReferralIcon from '@/assets/icons/menu/referral.svg?react';
import {useAuthStore} from '@/store/auth_store';
import {useRosterStore} from '@/store/roster_store';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import {FeatureToggle, GroupType} from "@/api/dto/dto";
import {useStateConfig} from "@/hooks/useStateConfig";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {
  DataPermissionLevel,
  DataPermissionLevelNew,
  DataSource,
  Feature,
  FeatureNew,
  FeaturePermissionLevel
} from "@/api/dto/permissions";

type Props = {
  hidePopover: () => void;
  showAdmissionModal: () => void;
  showReferralSsoModal: () => void;
  showReferralModal: () => void;
}

const CreateButtonContent = (props: Props) => {
  const {currentUser} = useAuthStore();
  const {currentRoster} = useRosterStore();
  const stateConfig = useStateConfig();
  const {checkBooleanToggle} = useFeatureToggles();

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const isAdmissionAvailable = useMemo(() => {
    return currentRoster
      && currentRoster.admittingFacility
      && !currentRoster.automated
      && currentRoster.type !== GroupType.CRISIS;
  }, [currentRoster]);

  const isAdmissionADTAvailable = currentUser && currentRoster && (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserDataPermissionRosterNew(
      currentUser,
      currentRoster,
      DataPermissionLevelNew.ACT,
      DataSource.ADT,
    ) : PermissionsHelpers.canForUserDataPermissionRoster(
      currentUser,
      currentRoster,
      DataPermissionLevel.WRITE,
      DataSource.ADT,
    ));
  const isReferralSsoAvailable = currentUser && currentRoster && (isNewPermissionsEnabled ? (
    PermissionsHelpers.canForUserDataPermissionRosterNew(
      currentUser,
      currentRoster,
      DataPermissionLevelNew.SSO,
      DataSource.FINDHELP,
    ) ||
    PermissionsHelpers.canForUserDataPermissionRosterNew(
      currentUser,
      currentRoster,
      DataPermissionLevelNew.SSO,
      DataSource.OB_REFERRALS,
    ) ||
    PermissionsHelpers.canForUserDataPermissionRosterNew(
      currentUser,
      currentRoster,
      DataPermissionLevelNew.SSO,
      DataSource.OB_CRISIS,
    ) ||
    currentUser?.roundtrip_sso
  ) : (
    PermissionsHelpers.canForUserDataPermissionRoster(
      currentUser,
      currentRoster,
      DataPermissionLevel.SSO,
      DataSource.FINDHELP,
    ) ||
    PermissionsHelpers.canForUserDataPermissionRoster(
      currentUser,
      currentRoster,
      DataPermissionLevel.SSO,
      DataSource.OB_REFERRALS,
    ) ||
    PermissionsHelpers.canForUserDataPermissionRoster(
      currentUser,
      currentRoster,
      DataPermissionLevel.SSO,
      DataSource.OB_CRISIS,
    ) ||
    currentUser?.roundtrip_sso
  ));

  const canActReferrals = currentUser && currentRoster && (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      FeatureNew.REFERRALS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      Feature.REFERRALS,
    )
  );

  const canViewReferrals = currentUser && currentRoster && (isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.VIEW,
      FeatureNew.REFERRALS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.VIEW,
      Feature.REFERRALS,
    )
  );

  const handleAdmissionClick = () => {
    if (isAdmissionAvailable) {
      props.showAdmissionModal();
      props.hidePopover();
    }
  };

  const handleReferralSsoClick = () => {
    if (isReferralSsoAvailable) {
      props.showReferralSsoModal();
      props.hidePopover();
    }
  };

  const isReferralsAvailable = canActReferrals || canViewReferrals;

  const handleReferralsClick = () => {
    if(isReferralsAvailable) {
      props.showReferralModal();
      props.hidePopover();
    }
  };

  return (
    <>
      <Menu onHide={props.hidePopover} width="28rem" marginTop marginBottom>
        <MenuItem disabled={!isAdmissionAvailable || !isAdmissionADTAvailable} onClick={handleAdmissionClick}>
          <MenuItemIcon iconClass={ICONS['user-circle']}/>
          <MenuItemTypography>Admission</MenuItemTypography>
          {(!isAdmissionAvailable || !isAdmissionADTAvailable) && (
            <>
              <MenuItemSpacer/>
              <ToolTip
                text={
                  isAdmissionADTAvailable === false
                    ? "Access to this feature is not available with your current role in the selected group."
                    : "Please select a group that is a manual admitting facility to enable this option."
                }
                position="bottom-start"
                type="dark"
              >
                <MenuItemTrailingIcon iconClass={ICONS['info-circle']}/>
              </ToolTip>
            </>
          )}


        </MenuItem>
        <MenuItem disabled={!isReferralSsoAvailable} onClick={handleReferralSsoClick}>
          <ReferralIcon style={menuItemIconStyle}/>
          <MenuItemTypography>Referrals SSO</MenuItemTypography>
          {!isReferralSsoAvailable && (
            <>
              <MenuItemSpacer/>
              <ToolTip
                text={currentRoster
                  ? `Users require ${stateConfig?.branding.openBeds.referral.service}, ${stateConfig?.branding.findHelp.service}, 
                  or ${stateConfig?.branding.roundTrip.service} SSO access to enable this option.`
                  : 'No group selected.'}
                position="bottom-start"
                type="dark"
              >
                <MenuItemTrailingIcon iconClass={ICONS['info-circle']}/>
              </ToolTip>
            </>
          )}
        </MenuItem>

        {checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS) && isReferralsAvailable &&
            <MenuItem disabled={!canActReferrals} onClick={handleReferralsClick}>
              <ReferralIcon style={menuItemIconStyle}/>
              <MenuItemTypography>Referrals</MenuItemTypography>
              {!canActReferrals && (
                <>
                  <MenuItemSpacer/>
                  <ToolTip
                    text={currentRoster
                      ? `User requires Referrals access to enable this option.`
                      : 'No group selected.'}
                    position="bottom-start"
                    type={"dark"}
                  >
                    <MenuItemTrailingIcon iconClass={ICONS['info-circle']}/>
                  </ToolTip>
                </>
              )}
            </MenuItem>}

      </Menu>
    </>
  );
};

export default CreateButtonContent;
